import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Sparkles, Tag, Calendar, Copy, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";
import { F, NvInputStyles, Skeleton, useConfirm } from "./_shared";

const emptyForm = () => ({
  title: "",
  subtitle: "",
  promo_code: "",
  flyer_file_id: null,
  flyer_url: "",
  vehicle_ids: [],
  hourly_rate: 250,
  min_hours: 3,
  starts_at: "",
  ends_at: "",
  active: true,
});

export default function SpecialsTab() {
  const [specials, setSpecials] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const load = async () => {
    try {
      const [s, v] = await Promise.all([
        api.get("/admin/specials"),
        api.get("/vehicles"),
      ]);
      setSpecials(s.data || []);
      setVehicles(v.data || []);
    } catch (e) {
      toast.error("Could not load specials");
      setSpecials([]);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(emptyForm());
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.promo_code.trim()) return toast.error("Promo code is required");
    if (!form.vehicle_ids.length) return toast.error("Pick at least one vehicle");
    if (!(Number(form.hourly_rate) > 0)) return toast.error("Hourly rate must be > $0");
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        subtitle: form.subtitle.trim() || null,
        promo_code: form.promo_code.trim().toLowerCase(),
        flyer_file_id: form.flyer_file_id,
        flyer_url: form.flyer_url || null,
        vehicle_ids: form.vehicle_ids,
        hourly_rate: Number(form.hourly_rate),
        min_hours: Number(form.min_hours),
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        active: !!form.active,
      };
      await api.post("/admin/specials", payload);
      toast.success("Special created");
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create special");
    } finally {
      setSaving(false);
    }
  };

  const uploadFlyer = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/specials/upload-flyer", fd);
      setForm((f) => ({ ...f, flyer_file_id: data.file_id, flyer_url: data.url }));
      toast.success("Flyer uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/admin/specials/${s.id}`, { active: !s.active });
      toast.success(s.active ? "Special disabled" : "Special activated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const remove = async (s) => {
    const ok = await confirm({
      title: "Delete this special?",
      message: "The promo link will stop working and the flyer will disappear from Specials.",
      meta: `${s.title} · ${s.promo_code}`,
      confirmLabel: "Delete special",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/specials/${s.id}`);
      toast.success("Special deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const copyLink = (s) => {
    const url = `${window.location.origin}/promo/${s.promo_code}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Link copied — paste it in your DMs or QR code"),
      () => toast.error("Could not copy"),
    );
  };

  if (specials === null) return <Skeleton />;

  return (
    <div data-testid="specials-tab" className="space-y-6">
      <NvInputStyles />

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-cyan" />
          <div className="font-serif text-2xl">Specials</div>
          <span className="label-caps text-white/40 text-[10px] ml-2">
            flyer promos · dynamic pricing
          </span>
        </div>
        <button
          type="button"
          data-testid="specials-new-btn"
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan text-midnight text-xs label-caps font-medium"
        >
          <Plus size={14} /> New Special
        </button>
      </div>

      {/* Empty state */}
      {specials.length === 0 && (
        <div className="glass rounded-2xl p-10 text-center border border-white/5">
          <Sparkles size={28} className="text-cyan mx-auto mb-3" />
          <div className="font-serif text-xl mb-1">No specials yet</div>
          <div className="text-white/50 text-sm mb-5">
            Upload a flyer, pick vehicles, and set a promo rate — customers who arrive
            from your flyer link get the special price automatically.
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan text-midnight text-xs label-caps"
          >
            <Plus size={14} /> Create your first special
          </button>
        </div>
      )}

      {/* List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {specials.map((s) => (
          <div
            key={s.id}
            data-testid={`special-card-${s.id}`}
            className="glass rounded-3xl overflow-hidden border border-white/5"
          >
            {s.flyer_url ? (
              <div className="aspect-[4/3] bg-ink-800 relative overflow-hidden">
                <img
                  src={s.flyer_url}
                  alt={s.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3 flex gap-2">
                  <span className={`text-[10px] label-caps rounded-full px-2 py-1 font-bold ${
                    s.is_live
                      ? "bg-lime-400 text-midnight"
                      : "bg-white/30 text-white"
                  }`}>
                    {s.is_live ? "LIVE" : s.active ? "SCHEDULED" : "OFF"}
                  </span>
                  <span className="text-[10px] label-caps rounded-full px-2 py-1 bg-cyan text-midnight font-bold">
                    ${s.hourly_rate}/hr
                  </span>
                </div>
              </div>
            ) : (
              <div className="aspect-[4/3] bg-ink-800 flex items-center justify-center text-white/30 text-sm">
                No flyer uploaded
              </div>
            )}

            <div className="p-5 space-y-3">
              <div>
                <div className="font-serif text-xl leading-tight">{s.title}</div>
                {s.subtitle && (
                  <div className="text-xs text-white/60 mt-0.5">{s.subtitle}</div>
                )}
              </div>

              <div className="flex flex-wrap gap-2 text-[11px]">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/5">
                  <Tag size={10} className="text-cyan" />{s.promo_code}
                </span>
                <span className="px-2 py-1 rounded-full bg-white/5">
                  {s.vehicle_ids?.length || 0} vehicle{s.vehicle_ids?.length === 1 ? "" : "s"}
                </span>
                <span className="px-2 py-1 rounded-full bg-white/5">
                  min {s.min_hours}h
                </span>
                {(s.starts_at || s.ends_at) && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/5">
                    <Calendar size={10} />
                    {s.starts_at ? new Date(s.starts_at).toLocaleDateString() : "—"}
                    {" → "}
                    {s.ends_at ? new Date(s.ends_at).toLocaleDateString() : "∞"}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  type="button"
                  data-testid={`special-toggle-${s.id}`}
                  onClick={() => toggleActive(s)}
                  className={`text-[11px] label-caps rounded-full px-3 py-1.5 border ${
                    s.active
                      ? "border-lime-400/40 text-lime-300 hover:bg-lime-400/10"
                      : "border-white/20 text-white/60 hover:bg-white/5"
                  }`}
                >
                  {s.active ? "Turn off" : "Turn on"}
                </button>
                <button
                  type="button"
                  onClick={() => copyLink(s)}
                  className="text-[11px] label-caps rounded-full px-3 py-1.5 border border-cyan/40 text-cyan hover:bg-cyan/10 inline-flex items-center gap-1"
                >
                  <Copy size={11} /> Copy link
                </button>
                <a
                  href={`/promo/${s.promo_code}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] label-caps rounded-full px-3 py-1.5 border border-white/20 text-white/70 hover:bg-white/5 inline-flex items-center gap-1"
                >
                  <ExternalLink size={11} /> Preview
                </a>
                <button
                  type="button"
                  data-testid={`special-delete-${s.id}`}
                  onClick={() => remove(s)}
                  className="text-[11px] label-caps rounded-full px-3 py-1.5 border border-rose-400/40 text-rose-300 hover:bg-rose-400/10 inline-flex items-center gap-1 ml-auto"
                >
                  <Trash2 size={11} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <form
            onSubmit={submit}
            data-testid="special-create-form"
            className="w-full max-w-2xl bg-[#0B1120] rounded-3xl border border-white/10 shadow-2xl p-6 sm:p-8 space-y-5 my-8"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="label-caps text-cyan text-[10px]">New Special</div>
                <div className="font-serif text-2xl">Create promo campaign</div>
              </div>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-white/50 hover:text-white text-2xl leading-none px-2"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <F label="Title *">
                <input
                  data-testid="special-title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Maybach Special"
                  className="nv-input"
                  required
                />
              </F>
              <F label="Promo code * (lowercase, no spaces)">
                <input
                  data-testid="special-code"
                  value={form.promo_code}
                  onChange={(e) => setForm({ ...form, promo_code: e.target.value.toLowerCase().replace(/[^a-z0-9\-_]/g, "") })}
                  placeholder="e.g. maybach250"
                  className="nv-input"
                  required
                />
              </F>
            </div>

            <F label="Subtitle (optional)">
              <input
                value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                placeholder="Limited-time offer · 3-hour minimum"
                className="nv-input"
              />
            </F>

            <F label="Flyer image">
              <div className="flex items-center gap-3">
                <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs label-caps cursor-pointer border ${
                  uploading ? "border-white/10 text-white/40" : "border-cyan/40 text-cyan hover:bg-cyan/10"
                }`}>
                  <Upload size={12} />
                  {uploading ? "Uploading…" : "Upload flyer"}
                  <input
                    type="file"
                    accept="image/*"
                    data-testid="special-flyer-input"
                    onChange={(e) => uploadFlyer(e.target.files?.[0])}
                    disabled={uploading}
                    className="hidden"
                  />
                </label>
                {form.flyer_url && (
                  <img src={form.flyer_url} alt="flyer preview"
                    className="w-16 h-16 rounded-lg object-cover border border-white/10" />
                )}
              </div>
            </F>

            <F label="Qualifying vehicles *">
              <div className="max-h-52 overflow-y-auto rounded-xl border border-white/10 p-2 space-y-1">
                {vehicles.map((v) => {
                  const checked = form.vehicle_ids.includes(v.id);
                  return (
                    <label
                      key={v.id}
                      className={`flex items-center gap-3 px-2 py-1.5 rounded-lg cursor-pointer text-sm ${
                        checked ? "bg-cyan/10 text-white" : "text-white/70 hover:bg-white/5"
                      }`}
                    >
                      <input
                        type="checkbox"
                        data-testid={`special-veh-${v.id}`}
                        checked={checked}
                        onChange={() => setForm((f) => ({
                          ...f,
                          vehicle_ids: checked
                            ? f.vehicle_ids.filter((id) => id !== v.id)
                            : [...f.vehicle_ids, v.id],
                        }))}
                        className="accent-cyan"
                      />
                      <span className="flex-1">{v.name}</span>
                      <span className="text-[11px] text-white/40">${v.hourly_rate}/hr</span>
                    </label>
                  );
                })}
              </div>
            </F>

            <div className="grid grid-cols-2 gap-4">
              <F label="Promo hourly rate ($) *">
                <input
                  type="number" min="1" step="1"
                  data-testid="special-rate"
                  value={form.hourly_rate}
                  onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                  className="nv-input"
                  required
                />
              </F>
              <F label="Minimum hours *">
                <input
                  type="number" min="1" step="1"
                  data-testid="special-min-hours"
                  value={form.min_hours}
                  onChange={(e) => setForm({ ...form, min_hours: e.target.value })}
                  className="nv-input"
                  required
                />
              </F>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <F label="Starts (optional)">
                <input
                  type="datetime-local"
                  value={form.starts_at}
                  onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                  className="nv-input"
                />
              </F>
              <F label="Ends (optional)">
                <input
                  type="datetime-local"
                  value={form.ends_at}
                  onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                  className="nv-input"
                />
              </F>
            </div>

            <label className="flex items-center gap-2 text-sm text-white/80">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="accent-cyan"
              />
              Active immediately
            </label>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 rounded-full text-xs label-caps text-white/60 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="special-submit"
                disabled={saving}
                className="px-5 py-2 rounded-full bg-cyan text-midnight text-xs label-caps font-medium disabled:opacity-50"
              >
                {saving ? "Saving…" : "Create special"}
              </button>
            </div>
          </form>
        </div>
      )}
      {confirmDialog}
    </div>
  );
}
